import { useState, useEffect, useCallback, useRef } from "react";
import { usePageTitle } from "../../hooks/usePageTitle";
import adminFetch from "../../utils/adminFetch";

const PAGE_SIZE = 50;

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "240px" }}>
      <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24"
        fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
}

function timeAgo(str) {
  const diff = Date.now() - new Date(str).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)   return `${days}d ago`;
  return new Date(str).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ── Delete confirmation modal ─────────────────────────────────────────
function DeleteModal({ sub, onClose, onConfirm, deleting }) {
  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(1,4,10,0.5)", backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        style={{ border: "1px solid rgba(0,45,181,0.12)" }}>
        <div className="px-7 py-5 flex items-center gap-4"
          style={{ background: "linear-gradient(135deg,#7f1d1d,#dc2626)" }}>
          <div className="w-10 h-10 rounded-full bg-white bg-opacity-15 flex items-center justify-center flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
            </svg>
          </div>
          <div>
            <p className="font-cinzel uppercase text-white opacity-60 tracking-widest" style={{ fontSize: "8px", letterSpacing: "0.28em" }}>Confirm Action</p>
            <h2 className="font-cinzel font-bold text-white uppercase" style={{ fontSize: "14px", letterSpacing: "0.06em" }}>Remove Subscriber</h2>
          </div>
        </div>
        <div className="px-7 py-6">
          <p className="font-constantia text-gray-600 leading-relaxed mb-1" style={{ fontSize: "14px" }}>
            Remove <strong style={{ color: "#012E72" }}>{sub.email}</strong> from the newsletter list?
          </p>
          <p className="font-constantia text-gray-400 mb-6" style={{ fontSize: "12px" }}>
            They will no longer receive newsletters. This cannot be undone.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={deleting}
              className="flex-1 font-cinzel uppercase border border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600 py-3 rounded-xl transition-all duration-200"
              style={{ fontSize: "9px", letterSpacing: "0.15em" }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={deleting}
              className="flex-1 font-cinzel uppercase text-white py-3 rounded-xl transition-all duration-200 disabled:opacity-60"
              style={{ fontSize: "9px", letterSpacing: "0.15em", background: "linear-gradient(135deg,#7f1d1d,#dc2626)" }}
            >
              {deleting ? "Removing…" : "Remove"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Source badge ──────────────────────────────────────────────────────
function SourceBadge({ source }) {
  const src = source || "footer";
  return (
    <span style={{
      background: "#EEF2F8",
      color: "#012E72",
      padding: "2px 8px",
      borderRadius: "20px",
      fontSize: "9px",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      fontWeight: 600,
      fontFamily: "Arial, sans-serif",
    }}>
      {src}
    </span>
  );
}

// ── Main ──────────────────────────────────────────────────────────────
export default function AdminNewsletter() {
  usePageTitle("Newsletter Subscribers");

  const [subscribers, setSubscribers] = useState([]);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [sort,        setSort]        = useState("newest");
  const [page,        setPage]        = useState(1);
  const [toDelete,    setToDelete]    = useState(null);
  const [deleting,    setDeleting]    = useState(false);
  const [error,       setError]       = useState(null);

  const searchTimer = useRef(null);

  const load = useCallback(async (q, s, p) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ q: q || "", sort: s, page: p, limit: PAGE_SIZE });
      const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/newsletter?${params}`);
      const data = await res.json();
      if (data.success) {
        setSubscribers(data.subscribers);
        setTotal(data.total);
      }
    } catch {
      setError("Failed to load subscribers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(search, sort, page); }, [sort, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearch(q);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); load(q, sort, 1); }, 300);
  };

  const handleSortChange = (s) => { setSort(s); setPage(1); };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await adminFetch(`${process.env.REACT_APP_API_URL}/api/newsletter/${toDelete._id}`, { method: "DELETE" });
      setToDelete(null);
      load(search, sort, page);
    } catch {
      setError("Failed to remove subscriber.");
    } finally {
      setDeleting(false);
    }
  };

  const exportCSV = () => {
    if (!subscribers.length) return;
    const header = "Email,Source,Subscribed Date\n";
    const rows = subscribers.map((s) =>
      `"${s.email}","${s.source || "footer"}","${new Date(s.createdAt).toLocaleDateString("en-US")}"`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `paladin-newsletter-subscribers-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const cell = {
    padding: "14px 16px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle",
  };

  return (
    <div style={{ maxWidth: "900px" }}>

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "22px", letterSpacing: "0.06em" }}>
            Newsletter Subscribers
          </h1>
          <p className="font-constantia mt-1" style={{ fontSize: "14px", color: "#94a3b8" }}>
            {total} {total === 1 ? "subscriber" : "subscribers"} in the list
          </p>
        </div>

        <button
          onClick={exportCSV}
          disabled={!subscribers.length}
          className="flex items-center gap-2 font-cinzel uppercase py-2.5 px-5 rounded-xl transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            fontSize: "10px",
            letterSpacing: "0.15em",
            background: "linear-gradient(135deg,#012E72,#002DB5)",
            color: "#fff",
            boxShadow: "0 4px 14px rgba(0,45,181,0.25)",
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Export CSV
        </button>
      </div>

      {/* ── Stat card ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total Subscribers", value: total, icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          )},
          { label: "This Month", value: subscribers.filter((s) => {
              const d = new Date(s.createdAt);
              const now = new Date();
              return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
            }).length, icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          )},
          { label: "Shown (Page)", value: subscribers.length, icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
          )},
        ].map(({ label, value, icon }) => (
          <div key={label} className="flex items-center gap-4 rounded-2xl px-5 py-4"
            style={{ background: "#FFFFFF", border: "1px solid rgba(1,46,114,0.08)" }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(1,46,114,0.07)", color: "#012E72" }}>
              {icon}
            </div>
            <div>
              <p className="font-cinzel font-bold text-[#012E72]" style={{ fontSize: "20px" }}>{value}</p>
              <p className="font-cinzel uppercase" style={{ fontSize: "9px", letterSpacing: "0.18em", color: "#94a3b8" }}>{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        {/* Search */}
        <div className="relative flex-1" style={{ minWidth: "200px" }}>
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" width="13" height="13"
            viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by email…"
            className="w-full pl-8 pr-4 py-2.5 font-constantia outline-none transition-all duration-150"
            style={{
              background: "#FFFFFF",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              fontSize: "13px",
              color: "#012E72",
            }}
          />
        </div>

        {/* Sort */}
        <div className="flex items-center gap-1 rounded-xl p-1" style={{ background: "#FFFFFF", border: "1px solid #e2e8f0" }}>
          {[
            { value: "newest", label: "Newest" },
            { value: "oldest", label: "Oldest" },
          ].map((s) => (
            <button
              key={s.value}
              onClick={() => handleSortChange(s.value)}
              className="font-cinzel uppercase px-4 py-1.5 rounded-lg transition-all duration-150"
              style={{
                fontSize: "10px",
                letterSpacing: "0.12em",
                background: sort === s.value ? "#012E72" : "transparent",
                color: sort === s.value ? "#fff" : "#94a3b8",
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-xl" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
          <p className="font-constantia text-red-600" style={{ fontSize: "13px" }}>{error}</p>
        </div>
      )}

      {/* ── Table ── */}
      <div className="rounded-2xl overflow-hidden" style={{ background: "#FFFFFF", border: "1px solid rgba(1,46,114,0.08)" }}>

        {/* Table header */}
        <div className="grid px-4 py-3" style={{ gridTemplateColumns: "1fr 90px 120px 40px", borderBottom: "2px solid #f1f5f9" }}>
          {["Email Address", "Source", "Joined", ""].map((h) => (
            <span key={h} className="font-cinzel uppercase" style={{ fontSize: "9px", letterSpacing: "0.2em", color: "#94a3b8" }}>
              {h}
            </span>
          ))}
        </div>

        {loading ? (
          <Spinner />
        ) : subscribers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            <p className="font-cinzel uppercase" style={{ fontSize: "10px", letterSpacing: "0.2em", color: "#cbd5e1" }}>
              {search ? "No subscribers match your search" : "No subscribers yet"}
            </p>
          </div>
        ) : (
          <div>
            {subscribers.map((sub, i) => (
              <div
                key={sub._id}
                className="grid items-center px-4 transition-colors duration-100"
                style={{
                  gridTemplateColumns: "1fr 90px 120px 40px",
                  borderBottom: i < subscribers.length - 1 ? "1px solid #f8fafc" : "none",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f7f9ff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                {/* Email */}
                <div style={cell}>
                  <p className="font-constantia text-[#012E72]" style={{ fontSize: "13px" }}>{sub.email}</p>
                </div>

                {/* Source */}
                <div style={cell}>
                  <SourceBadge source={sub.source} />
                </div>

                {/* Date */}
                <div style={cell}>
                  <p className="font-constantia" style={{ fontSize: "12px", color: "#94a3b8" }}>
                    {timeAgo(sub.createdAt)}
                  </p>
                  <p className="font-constantia" style={{ fontSize: "10px", color: "#cbd5e1" }}>
                    {new Date(sub.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                </div>

                {/* Delete */}
                <div style={{ ...cell, borderBottom: "none", padding: "14px 8px" }}>
                  <button
                    onClick={() => setToDelete(sub)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150"
                    title="Remove subscriber"
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#FEF2F2"; e.currentTarget.style.color = "#dc2626"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#cbd5e1"; }}
                    style={{ color: "#cbd5e1" }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      <path d="M10 11v6"/><path d="M14 11v6"/>
                      <path d="M9 6V4h6v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-4" style={{ borderTop: "1px solid #f1f5f9" }}>
            <p className="font-constantia" style={{ fontSize: "12px", color: "#94a3b8" }}>
              Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="font-cinzel uppercase px-4 py-1.5 rounded-lg border transition-all duration-150 disabled:opacity-30"
                style={{ fontSize: "9px", letterSpacing: "0.12em", borderColor: "#e2e8f0", color: "#012E72" }}
              >
                ← Prev
              </button>
              <span className="font-cinzel" style={{ fontSize: "10px", color: "#94a3b8" }}>{page} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="font-cinzel uppercase px-4 py-1.5 rounded-lg border transition-all duration-150 disabled:opacity-30"
                style={{ fontSize: "9px", letterSpacing: "0.12em", borderColor: "#e2e8f0", color: "#012E72" }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete modal */}
      {toDelete && (
        <DeleteModal
          sub={toDelete}
          onClose={() => setToDelete(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
    </div>
  );
}
